import React from 'react';
import { useRTL } from '../shared/hooks/useRTL';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const PrivacyPolicy = () => {
  const { isRTL } = useRTL();

  // Scroll to section functionality
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get hover animation class based on RTL
  const getHoverAnimClass = () => {
    return isRTL ? 'group-hover:translate-x-[4px]' : 'group-hover:translate-x-[-4px]';
  };

  const sections = [
    { id: 'introduction', title: '📋 مقدمة', icon: '📋' },
    { id: 'data-collection', title: '🔐 1. المعلومات التي نجمعها', icon: '🔐' },
    { id: 'data-usage', title: '💾 2. كيفية استخدام المعلومات', icon: '💾' },
    { id: 'data-security', title: '🔒 3. أمان البيانات', icon: '🔒' },
    { id: 'data-sharing', title: '📤 4. مشاركة المعلومات', icon: '📤' },
    { id: 'data-management', title: '🔄 5. إدارة البيانات', icon: '🔄' },
    { id: 'data-retention', title: '🕐 6. مدة الاحتفاظ بالبيانات', icon: '🕐' },
    { id: 'location', title: '🌍 7. الموقع الجغرافي', icon: '🌍' },
    { id: 'notifications', title: '🔔 8. الإشعارات', icon: '🔔' },
    { id: 'children', title: '👶 9. خصوصية الأطفال', icon: '👶' },
    { id: 'closed-loop', title: '🔐 10. النظام المغلق', icon: '🔐' },
    { id: 'contact', title: '📞 11. الاتصال بنا', icon: '📞' },
    { id: 'consent', title: '✅ 12. موافقتك', icon: '✅' },
    { id: 'summary', title: '📋 13. ملخص سريع', icon: '📋' },
  ];

  return (
    <div className={`min-h-screen bg-background py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <PageHeader
          className="mb-12 flex-col items-center text-center sm:items-center"
          title="سياسة الخصوصية - تطبيق TAG"
          description={
            <span className="inline-flex items-center gap-2">
              <span>آخر تحديث:</span>
              <Badge variant="secondary">يناير 2025</Badge>
            </span>
          }
          actions={
            <div className="flex size-24 items-center justify-center rounded-full bg-primary/10 text-5xl">
              🔒
            </div>
          }
        />

        <div className="lg:flex lg:gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="mb-8 lg:mb-0 lg:w-64 lg:flex-shrink-0">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">📑 محتويات الصفحة</CardTitle>
              </CardHeader>
              <CardContent>
                <Separator className="mb-4" />
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <Button
                      key={section.id}
                      type="button"
                      variant="ghost"
                      className="group h-auto w-full justify-start px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className={`${getHoverAnimClass()} inline-block transition-transform duration-200`}>
                        {section.title}
                      </span>
                    </Button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <div className="lg:flex-1">
            {/* Introduction */}
            <Card id="introduction" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">📋</span>
                  مقدمة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-lg leading-relaxed text-foreground">
                  نرحب بك في تطبيق TAG. نحن ملتزمون بحماية خصوصيتك وأمان معلوماتك الشخصية. تُوضح هذه السياسة كيفية جمع واستخدام وحماية بياناتك عند استخدام تطبيق TAG.
                </p>
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                  <p className="font-semibold text-foreground">
                    <strong>⚡ من خلال استخدام التطبيق، أنت توافق على سياسة الخصوصية هذه.</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data Collection */}
            <Card id="data-collection" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🔐</span>
                  1. المعلومات التي نجمعها
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-foreground">
                    <span className="text-2xl">👤</span>
                    1.1 المعلومات الشخصية
                  </h3>
                  <p className="mb-3 text-muted-foreground">
                    نحتاج إلى جمع المعلومات التالية لتقديم خدماتنا:
                  </p>
                  <ul className="space-y-2 text-foreground list-none">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">✓</span>
                      <strong>معلومات الهوية:</strong> الاسم، رقم الهاتف، البريد الإلكتروني
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">✓</span>
                      <strong>معلومات الدفع:</strong> بيانات بطاقات الدفع (معالجة من خلال PayTabs المعتمدة)
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">✓</span>
                      <strong>معلومات الحساب:</strong> رقم المستخدم، كلمة المرور، رمز التحقق
                    </li>
                  </ul>
                </div>

                <Card className="bg-muted/50">
                  <CardContent className="pt-6">
                    <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-foreground">
                      <span className="text-2xl">💳</span>
                      1.2 معلومات المعاملات المالية
                    </h3>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-start gap-3">
                        <span className="mt-1 text-emerald-500">•</span>
                        سجل العمليات المالية (الشحن، الشراء، الدفع)
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 text-emerald-500">•</span>
                        تفاصيل الباقات المُشتراة والمُفعّلة
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 text-emerald-500">•</span>
                        المبالغ المدفوعة والمخصومة
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 text-emerald-500">•</span>
                        تاريخ ووقت كل معاملة
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <div>
                  <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-foreground">
                    <span className="text-2xl">📊</span>
                    1.3 معلومات الاستخدام
                  </h3>
                  <ul className="space-y-2 text-foreground">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">→</span>
                      نشاط التطبيق (الفتح، الاستخدام، الميزات المُستخدمة)
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">→</span>
                      معلومات الجهاز (نوع الجهاز، نظام التشغيل، رقم الجهاز)
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">→</span>
                      معلومات الموقع الجغرافي (عند استخدام QR Code للأمان)
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 text-primary">→</span>
                      سجل المسح (QR Scan logs) للأمان
                    </li>
                  </ul>
                </div>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="pt-6">
                    <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-foreground">
                      <span className="text-2xl">🛡️</span>
                      1.4 معلومات أمنية
                    </h3>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-start gap-3">
                        <span className="mt-1">🔍</span>
                        محاولات تسجيل الدخول
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1">🔍</span>
                        محاولات الدفع الفاشلة
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1">🔍</span>
                        السلوكيات المشبوهة (تسجيل في qr_security_logs)
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Data Usage */}
            <Card id="data-usage" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">💾</span>
                  2. كيفية استخدام المعلومات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        2.1 تقديم الخدمات
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• معالجة المعاملات المالية</li>
                        <li>• تفعيل وإدارة الباقات</li>
                        <li>• حساب الخصومات التلقائية</li>
                        <li>• إرسال الإشعارات الفورية</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-destructive/20 bg-destructive/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        2.2 الأمان والحماية
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• منع الاحتيال والاستخدام غير المصرح به</li>
                        <li>• مراقبة السلوكيات المشبوهة</li>
                        <li>• التحقق من هوية المستخدم</li>
                        <li>• تسجيل سجلات الأمان</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        2.3 تحسين الخدمة
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• تحليل استخدام التطبيق</li>
                        <li>• تحسين تجربة المستخدم</li>
                        <li>• تطوير الميزات الجديدة</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        2.4 التواصل
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• إرسال إشعارات عن المعاملات</li>
                        <li>• إرسال التحديثات والإعلانات</li>
                        <li>• الرد على استفساراتك</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card id="data-security" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🔒</span>
                  3. أمان البيانات
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Card className="border-emerald-500/30 bg-emerald-500/5">
                  <CardContent className="pt-6">
                    <h3 className="mb-4 text-xl font-semibold text-foreground">
                      3.1 التدابير التقنية
                    </h3>
                    <p className="mb-4 text-muted-foreground">
                      نستخدم تقنيات أمان متقدمة لحماية بياناتك:
                    </p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">🔐</span>
                        <div>
                          <strong className="text-foreground">التشفير:</strong>
                          <p className="text-sm text-muted-foreground">جميع البيانات المالية مشفّرة</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">🎫</span>
                        <div>
                          <strong className="text-foreground">مصادقة آمنة:</strong>
                          <p className="text-sm text-muted-foreground">Supabase Auth مع JWT tokens</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">⏱️</span>
                        <div>
                          <strong className="text-foreground">جلسات مؤقتة:</strong>
                          <p className="text-sm text-muted-foreground">جلسات الدفع تنتهي خلال دقائق</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">📱</span>
                        <div>
                          <strong className="text-foreground">QR Code مُشفّر:</strong>
                          <p className="text-sm text-muted-foreground">كل كود QR يحتوي على hash فريد</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/50">
                  <CardContent className="pt-6">
                    <h3 className="mb-4 text-xl font-semibold text-foreground">
                      3.2 الحماية من الاحتيال
                    </h3>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="text-primary">🛡️</span>
                        مراقبة محاولات استخدام QR Code أكثر من مرة
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-primary">🛡️</span>
                        تتبع المسح من مواقع جغرافية غير متوقعة
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-primary">🛡️</span>
                        تسجيل محاولات الدفع الفاشلة المتكررة
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-primary">🛡️</span>
                        حساب "نقاط الأمان" لكل مستخدم/تاجر
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card id="data-sharing" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">📤</span>
                  4. مشاركة المعلومات
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-4 text-xl font-semibold text-foreground">
                    4.1 مقدمي الخدمات
                  </h3>
                  <p className="mb-4 text-muted-foreground">
                    نشارك المعلومات مع مقدمي الخدمات الموثوقين:
                  </p>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card className="border-primary/20 bg-primary/5">
                      <CardContent className="pt-6">
                        <div className="mb-2 text-3xl">💳</div>
                        <strong className="text-foreground">PayTabs</strong>
                        <p className="mt-1 text-xs text-muted-foreground">Apple Pay, مدى, Visa</p>
                      </CardContent>
                    </Card>
                    <Card className="border-emerald-500/20 bg-emerald-500/5">
                      <CardContent className="pt-6">
                        <div className="mb-2 text-3xl">🗄️</div>
                        <strong className="text-foreground">Supabase</strong>
                        <p className="mt-1 text-xs text-muted-foreground">قاعدة البيانات والاستيثاق</p>
                      </CardContent>
                    </Card>
                    <Card className="border-amber-500/20 bg-amber-500/5">
                      <CardContent className="pt-6">
                        <div className="mb-2 text-3xl">🔥</div>
                        <strong className="text-foreground">Firebase</strong>
                        <p className="mt-1 text-xs text-muted-foreground">الإشعارات (FCM)</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="pt-6">
                    <h3 className="mb-4 text-xl font-semibold text-foreground">
                      4.2 المتطلبات القانونية
                    </h3>
                    <p className="text-muted-foreground">
                      قد نكشف عن معلوماتك في الحالات التالية: عند وجود متطلب قانوني، لحماية حقوقنا القانونية، أو للامتثال للقوانين والأنظمة المحلية.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-primary bg-primary text-primary-foreground">
                  <CardContent className="pt-6 text-center">
                    <h3 className="mb-2 text-2xl font-bold">4.3 لا نبيع بياناتك</h3>
                    <p className="text-lg">
                      <strong>لا نبيع أو نؤجر بياناتك الشخصية لأطراف ثالثة لأغراض تسويقية.</strong>
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Data Management */}
            <Card id="data-management" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🔄</span>
                  5. إدارة البيانات
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-4 text-xl font-semibold text-foreground">
                    5.1 حقوقك
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {[
                      { icon: '👁️', title: 'الوصول', desc: 'الاطلاع على بياناتك الشخصية' },
                      { icon: '✏️', title: 'التصحيح', desc: 'تصحيح المعلومات غير الصحيحة' },
                      { icon: '🗑️', title: 'الحذف', desc: 'طلب حذف بياناتك' },
                      { icon: '🚫', title: 'الاعتراض', desc: 'الاعتراض على استخدام بياناتك' },
                    ].map((right, idx) => (
                      <Card key={idx} className="bg-muted/50 transition-colors hover:bg-muted">
                        <CardContent className="pt-4">
                          <div className="mb-2 flex items-center gap-3">
                            <span className="text-2xl">{right.icon}</span>
                            <strong className="text-foreground">{right.title}</strong>
                          </div>
                          <p className="text-sm text-muted-foreground">{right.desc}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="pt-6">
                    <h3 className="mb-2 text-xl font-semibold text-foreground">
                      5.3 بيانات المعاملات المالية
                    </h3>
                    <p className="font-semibold text-foreground">
                      ⚠️ ملاحظة مهمة: سجلات المعاملات المالية يجب الاحتفاظ بها بموجب القانون، ولا يمكن حذفها.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card id="data-retention" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🕐</span>
                  6. مدة الاحتفاظ بالبيانات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                      <div className="mb-3 text-4xl">👤</div>
                      <strong className="text-foreground">6.1 البيانات الشخصية</strong>
                      <p className="mt-2 text-sm text-muted-foreground">
                        نحتفظ ببياناتك طالما أن حسابك نشط، أو حسب المتطلبات القانونية.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="pt-6">
                      <div className="mb-3 text-4xl">📊</div>
                      <strong className="text-foreground">6.2 سجلات المعاملات</strong>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Ledger: تُحفظ بصفة دائمة (مطلوبة قانونيًا)
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardContent className="pt-6">
                      <div className="mb-3 text-4xl">🛡️</div>
                      <strong className="text-foreground">6.3 السجلات الأمنية</strong>
                      <p className="mt-2 text-sm text-muted-foreground">
                        تُحفظ لفترة محددة للمساعدة في التحقيقات.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card id="location" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🌍</span>
                  7. الموقع الجغرافي
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Card className="bg-muted/50">
                  <CardContent className="pt-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">
                      7.1 متى نستخدم الموقع
                    </h3>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-500">✓</span>
                        عند مسح QR Code للأمان والتحقق من الموقع
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-primary">✓</span>
                        لتوفير عروض قريبة منك (اختياري)
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card id="notifications" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🔔</span>
                  8. الإشعارات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    { icon: '💳', title: 'إشعارات المعاملات', desc: 'دفع ناجح، شحن، إلخ' },
                    { icon: '🎁', title: 'إشعارات الباقات', desc: 'تفعيل، انتهاء' },
                    { icon: '🛡️', title: 'إشعارات الأمان', desc: 'محاولات تسجيل دخول' },
                    { icon: '📢', title: 'إشعارات التحديثات', desc: 'اختياري' },
                  ].map((notif, idx) => (
                    <Card key={idx} className="border-primary/20 bg-primary/5">
                      <CardContent className="pt-4">
                        <div className="mb-2 text-3xl">{notif.icon}</div>
                        <strong className="text-foreground">{notif.title}</strong>
                        <p className="mt-1 text-sm text-muted-foreground">{notif.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Children */}
            <Card id="children" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">👶</span>
                  9. خصوصية الأطفال
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="pt-6">
                    <p className="mb-2 text-lg font-bold text-foreground">
                      TAG غير مخصص للأطفال دون سن 18 عامًا.
                    </p>
                    <p className="text-muted-foreground">
                      إذا علمنا أننا جمعنا معلومات من طفل دون السن القانوني دون موافقة الوالدين، سنقوم بحذف هذه المعلومات فورًا.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Closed Loop System */}
            <Card id="closed-loop" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">🔐</span>
                  10. النظام المغلق (Closed-Loop System)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Card className="border-primary bg-primary text-primary-foreground">
                  <CardContent className="pt-6">
                    <h3 className="mb-3 text-xl font-bold">10.1 ما هو النظام المغلق؟</h3>
                    <p className="mb-4">
                      TAG هو محفظة رقمية مغلقة، مما يعني:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span>❌</span> <strong>لا يمكن سحب الأموال:</strong> الرصيد لا يمكن تحويله إلى حساب مصرفي خارجي
                      </li>
                      <li className="flex items-start gap-2">
                        <span>❌</span> <strong>لا يمكن التحويل:</strong> لا يمكن تحويل الأموال بين المستخدمين
                      </li>
                      <li className="flex items-start gap-2">
                        <span>✓</span> <strong>استخدام محدود:</strong> الرصيد يُستخدم فقط مع التجار المعتمدين
                      </li>
                      <li className="flex items-start gap-2">
                        <span>⚠️</span> <strong>الباقات غير قابلة للاسترداد:</strong> لا يمكن استردادها نقدًا
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <div>
                  <h3 className="mb-3 text-lg font-semibold text-foreground">
                    10.2 لماذا؟
                  </h3>
                  <p className="text-muted-foreground">
                    هذا التصميم يضمن أن TAG لا يُعتبر نظامًا مصرفيًا مرخصًا، مما يوفر الامتثال القانوني، الأمان، والبساطة في تجربة الدفع.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card id="contact" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">📞</span>
                  11. الاتصال بنا
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        11.1 للأسئلة أو المخاوف
                      </h3>
                      <ul className="space-y-2 text-foreground">
                        <li>📧 <strong>البريد الإلكتروني:</strong> privacy@tag.app</li>
                        <li>📱 <strong>الهاتف:</strong> [رقم الهاتف]</li>
                        <li>💬 <strong>الدعم الفني:</strong> داخل التطبيق → الدعم</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                      <h3 className="mb-3 text-lg font-semibold text-foreground">
                        11.2 التحديثات على السياسة
                      </h3>
                      <p className="mb-2 text-sm text-muted-foreground">
                        قد نحدّث هذه السياسة من وقت لآخر. سنخطرك بأي تغييرات مهمة عبر:
                      </p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>🔔 إشعار في التطبيق</li>
                        <li>📧 البريد الإلكتروني</li>
                        <li>📅 تحديث التاريخ في أعلى الصفحة</li>
                      </ul>
                      <p className="mt-3 text-sm font-semibold text-foreground">
                        يُنصح بمراجعة هذه السياسة بانتظام.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Consent */}
            <Card id="consent" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">✅</span>
                  12. موافقتك
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Card className="border-primary bg-primary text-primary-foreground">
                  <CardContent className="pt-6 text-center">
                    <p className="mb-6 text-2xl font-bold">
                      باستخدام تطبيق TAG، أنت توافق على:
                    </p>
                    <div className="grid gap-4 text-right md:grid-cols-2">
                      <div className="flex items-start gap-2 rounded-lg bg-primary-foreground/10 p-4">
                        <span className="text-2xl">✓</span>
                        <p className="text-sm">جمع واستخدام معلوماتك كما هو موضح في هذه السياسة</p>
                      </div>
                      <div className="flex items-start gap-2 rounded-lg bg-primary-foreground/10 p-4">
                        <span className="text-2xl">✓</span>
                        <p className="text-sm">شروط النظام المغلق (عدم إمكانية السحب أو التحويل)</p>
                      </div>
                      <div className="flex items-start gap-2 rounded-lg bg-primary-foreground/10 p-4">
                        <span className="text-2xl">✓</span>
                        <p className="text-sm">سياسة الأمان والاحتفاظ بالبيانات</p>
                      </div>
                      <div className="flex items-start gap-2 rounded-lg bg-primary-foreground/10 p-4">
                        <span className="text-2xl">✓</span>
                        <p className="text-sm">تلقي الإشعارات والتحديثات</p>
                      </div>
                    </div>
                    <p className="mt-6 text-lg font-bold">
                      إذا كنت لا توافق على هذه السياسة، يُرجى عدم استخدام التطبيق.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card id="summary" className="mb-6 scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <span className="text-3xl">📋</span>
                  13. ملخص سريع
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                      <CardContent className="pt-4">
                        <h3 className="mb-2 font-bold text-emerald-700 dark:text-emerald-400">✅ ما نجمع:</h3>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>• معلومات الهوية والدفع</li>
                          <li>• سجل المعاملات المالية</li>
                          <li>• معلومات الاستخدام والموقع (للسلامة)</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-primary/30 bg-primary/5">
                      <CardContent className="pt-4">
                        <h3 className="mb-2 font-bold text-foreground">✅ كيف نستخدم:</h3>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>• لتقديم الخدمات</li>
                          <li>• للأمان والحماية من الاحتيال</li>
                          <li>• لتحسين التطبيق</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-primary/30 bg-primary/5">
                      <CardContent className="pt-4">
                        <h3 className="mb-2 font-bold text-foreground">✅ الأمان:</h3>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>• تشفير البيانات</li>
                          <li>• جلسات مؤقتة</li>
                          <li>• مراقبة السلوك المشبوه</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-4">
                    <Card className="border-amber-500/30 bg-amber-500/5">
                      <CardContent className="pt-4">
                        <h3 className="mb-2 font-bold text-amber-700 dark:text-amber-400">✅ حقوقك:</h3>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>• الوصول والتصحيح والحذف</li>
                          <li>• التحكم بالإشعارات</li>
                          <li>• الاعتراض على الاستخدام</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-destructive/30 bg-destructive/5">
                      <CardContent className="pt-4">
                        <h3 className="mb-2 font-bold text-destructive">⚠️ ملاحظات مهمة:</h3>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>• <strong>النظام مغلق:</strong> لا يمكن سحب الرصيد أو تحويله</li>
                          <li>• <strong>الباقات غير قابلة للاسترداد نقدًا</strong></li>
                          <li>• <strong>سجلات المعاملات:</strong> تُحفظ بصفة دائمة (مطلوبة قانونيًا)</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Footer */}
            <Card className="border-primary bg-primary text-primary-foreground">
              <CardContent className="py-8 text-center">
                <div className="mb-4 text-6xl">🙏</div>
                <h2 className="mb-4 text-3xl font-bold">شكرًا لثقتك في TAG</h2>
                <p className="text-lg opacity-90">
                  © 2025 TAG. جميع الحقوق محفوظة.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Back to top button */}
        <Button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed bottom-8 z-50 size-12 rounded-full ${isRTL ? 'left-8' : 'right-8'}`}
          size="icon"
          aria-label="العودة للأعلى"
        >
          <span className="text-2xl">↑</span>
        </Button>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
